import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/axios';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function Invoice() {
  const { settings } = useSettings();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/orders/${id}/invoice`)
      .then((response) => setData(response.data))
      .catch((requestError) => setError(requestError.response?.data?.message || 'Invoice could not be loaded'));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E8DFD8] rounded-lg p-8 text-center space-y-4 max-w-md">
          <h1 className="font-heading text-2xl font-bold">Invoice Unavailable</h1>
          <p className="text-sm text-[#8A796C]">{error}</p>
          <Link to={`/orders/${id}`} className="btn-outline inline-block">Back to Order</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-sm text-[#8A796C]">Preparing invoice...</div>;

  const { order, seller } = data;
  return (
    <div className="invoice-page bg-[#F5EFEB] py-8 px-3 sm:px-4 min-h-screen">
      <div className="no-print max-w-4xl mx-auto mb-4 flex justify-between items-center gap-3">
        <Link to={`/orders/${order._id}`} className="btn-outline">Back to Order</Link>
        <button type="button" onClick={() => window.print()} className="btn-primary">Print / Save PDF</button>
      </div>

      <article className="invoice-print max-w-4xl mx-auto bg-white border border-[#D9CFC6] shadow-sm p-5 sm:p-8 md:p-12 text-[#2B1810]">
        <header className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 pb-7 border-b-2 border-[#2B1810]">
          <div>
            <img src={resolveMedia(settings?.logo || DEFAULT_LOGO)} alt={seller.name} className="h-16 w-auto max-w-[260px] object-contain object-left" />
            <p className="text-xs text-[#8A796C] mt-1">Tax Invoice / Payment Receipt</p>
            {seller.address && <p className="text-xs text-[#6B5B50] mt-3 max-w-sm">{seller.address}</p>}
            <p className="text-xs text-[#6B5B50]">{[seller.email, seller.phone].filter(Boolean).join(' | ')}</p>
          </div>
          <div className="sm:text-right text-xs space-y-1">
            <p className="font-heading text-xl font-bold text-[#C49A45]">INVOICE</p>
            <p><strong>Invoice:</strong> {order.invoiceNumber}</p>
            <p><strong>Issued:</strong> {new Date(order.invoiceIssuedAt).toLocaleDateString('en-IN')}</p>
            <p><strong>Order:</strong> {order.orderNumber}</p>
          </div>
        </header>

        <section className="grid sm:grid-cols-2 gap-6 py-7 text-xs">
          <div>
            <p className="font-bold uppercase mb-2 text-[#8A796C]">Billed To</p>
            <p className="font-bold text-sm">{order.shippingAddress.fullName}</p>
            <p className="leading-relaxed mt-1">
              {order.shippingAddress.line1}{order.shippingAddress.line2 ? `, ${order.shippingAddress.line2}` : ''}<br />
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}<br />
              India<br />
              {order.shippingAddress.phone}
            </p>
          </div>
          <div className="sm:text-right">
            <p className="font-bold uppercase mb-2 text-[#8A796C]">Payment</p>
            <p className="capitalize"><strong>Method:</strong> {order.paymentMethod.replace('_', ' ')}</p>
            <p><strong>Status:</strong> Paid</p>
            <p><strong>Currency:</strong> INR</p>
          </div>
        </section>

        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[560px]">
            <thead className="bg-[#2B1810] text-white uppercase">
              <tr>
                <th className="text-left p-3">Item</th>
                <th className="text-right p-3">Price</th>
                <th className="text-right p-3">Qty</th>
                <th className="text-right p-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8DFD8]">
              {order.items.map((item, index) => (
                <tr key={`${item.product}-${index}`}>
                  <td className="p-3">
                    <span className="font-bold">{item.name}</span>
                    {item.variantLabel && <span className="block text-[10px] text-[#8A796C]">{item.variantLabel}</span>}
                  </td>
                  <td className="p-3 text-right">{money(item.price)}</td>
                  <td className="p-3 text-right">{item.qty}</td>
                  <td className="p-3 text-right font-bold">{money(item.price * item.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <section className="ml-auto max-w-sm mt-7 text-xs space-y-2">
          <div className="flex justify-between"><span>Items total</span><span>{money(order.itemsTotal)}</span></div>
          <div className="flex justify-between"><span>Shipping and handling</span><span>{money(order.shippingCost)}</span></div>
          {order.discountTotal > 0 && <div className="flex justify-between"><span>Discount{order.promoCode ? ` (${order.promoCode})` : ''}</span><span>-{money(order.discountTotal)}</span></div>}
          {order.taxTotal > 0 && <div className="flex justify-between"><span>Tax</span><span>{money(order.taxTotal)}</span></div>}
          <div className="flex justify-between text-base font-bold border-t-2 border-[#2B1810] pt-3 mt-3">
            <span>Total Paid</span><span>{money(order.totalAmount)}</span>
          </div>
        </section>

        <footer className="mt-10 pt-5 border-t border-[#E8DFD8] text-center text-[10px] text-[#8A796C]">
          This invoice was generated electronically after successful payment.
        </footer>
      </article>
    </div>
  );
}
